/**
 * @mixin validateFormMixin
 * @author pierre-michel@on-running.com
 *
 * Standardize the way we handle frontend validation (global error message below the submit button
 * "Please correct the fields marked in red." and specific error messages below the
 * targeted fields. Validation on change of input value will only happen if the form has been
 * submitted at least once or if `validateBeforeSubmit` is true.
 *
 * NOTE: You don't have to use this mixin for frontend validation if the form is not standard and
 * should handle errors in a different way.
 */

import submitFormMixin from './submit-form'
import formUtilsMixin from './form-utils'

export default {
  mixins: [submitFormMixin, formUtilsMixin],

  data() {
    return {
      formData: {},
      fieldsArray: [],
      fieldsMarkedAsValid: {},
      errorCount: 0,
      hasChangeHandlers: false,
      validateBeforeSubmit: false,
    }
  },

  props: {
    syncFormData: {
      type: Function,
      default: () => {},
    },
  },

  watch: {
    hasChangeHandlers() {
      this.updateFieldsArrayValidation()
    },

    validateBeforeSubmit() {
      this.updateFieldsArrayValidation()
    },
  },

  methods: {
    resetForm() {
      this.resetFormBase()
    },

    resetFormBase() {
      this.errorCount = 0
      this.hasChangeHandlers = false
      this.fieldsMarkedAsValid = {}
    },

    formatFieldsArray(fields) {
      fields.forEach(field => {
        field.errors = field.errors || []
        field.on = field.on || {}

        if (this.hasChangeHandlers || this.validateBeforeSubmit) {
          field.validateOnChange = () => this.validateField(field, true)
        }
      })
      return fields
    },

    updateFieldsArrayValidation() {
      this.fieldsArray = this.formatFieldsArray(this.fieldsArray)
    },

    updateField(fieldKey, getObjToMerge) {
      const index = this.fieldsArray.findIndex(field => field.key == fieldKey)
      const fieldObj = this.fieldsArray[index]
      const newFieldObj = Object.assign({}, fieldObj, getObjToMerge(fieldObj))

      this.$set(this.fieldsArray, index, newFieldObj)
    },

    validateForm(formSubmitHandler) {
      this.hasChangeHandlers = false
      this.validateBeforeSubmit = false
      this.fieldsArray.forEach(field => this.validateField(field))

      if (formSubmitHandler) {
        if (this.errorCount) {
          this.hasSubmitError = false
          this.submittedSuccessfully = false
          this.fixScrollRelativeToSubmitCta()
        } else {
          formSubmitHandler()
        }
      }
      this.syncFormDataOnNextTick()
      return this.formStatus
    },

    validateField(field, fromChangeHandler) {
      const value = this.formData[field.key]
      const previousErrorCount = field.errors.length
      field.errors = []
      field.hasSpecificError = false

      const isEmpty = this.isEmpty(value)
      const shouldNotBeEmpty = !field.optional && !field.hidden
      const highlightOnlyIfNotEmpty =
        fromChangeHandler && !this.fieldsMarkedAsValid[field.key] && !this.hasChangeHandlers
      let isInvalid = false

      if (!isEmpty && !field.hidden && field.isInvalid) {
        isInvalid = field.isInvalid(value)
      }
      if (isEmpty && shouldNotBeEmpty && !highlightOnlyIfNotEmpty) {
        field.errors.push(this.$t('errors.required_field'))
      }
      if (isInvalid) {
        const isString = typeof isInvalid == 'string'
        const errorMsg = isString ? isInvalid : this.$t('errors.invalid_field')
        field.hasSpecificError = isString
        field.errors.push(errorMsg)
      }
      if (!field.optional || !isEmpty) {
        this.fieldsMarkedAsValid[field.key] = !field.errors.length
      }
      this.errorCount += field.errors.length - previousErrorCount

      if (field.errors.length) this.hasChangeHandlers = true
      if (fromChangeHandler) this.validateGroupMembers(field)
    },

    validateGroupMembers(fieldChanging) {
      if (!fieldChanging.groups) {
        return
      }
      this.fieldsArray.find(field => {
        if (field.groups && fieldChanging.key != field.key) {
          const hasGroupMatching = fieldChanging.groups.some(
            group => field.groups.indexOf(group) >= 0
          )

          if (hasGroupMatching) {
            this.validateField(field)
          }
        }
      })
    },

    syncFormDataOnNextTick() {
      this.$nextTick(() => this.syncFormData(this.formData, this.formStatus))
    },
  },
}
